import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, EventEmitter, inject, Input, OnDestroy, Output, Signal, untracked } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { isRouteAllowed } from 'src/app/classes/shared/route-tabs';
import { UiTool } from 'src/app/classes/shared/ui.tools';
import { DashboardManagerComponent } from 'src/app/components/project-pages/dashboard/dialogs/dashboard-manager/dashboard-manager.component';
import { defaultProject, Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { DashboardStore } from 'src/app/services/stores/dashboard.store';
import { SidenavStore } from 'src/app/services/stores/sidenav.store';
import { IndicatorComponent, NestedNavigationComponent, TreeNode } from 'ui-library';
import { SocketUserListComponent } from '../../common/socket-user-list/socket-user-list.component';
import { MainMenu } from '../header/MainMenu';
import { SideMenu } from './SideMenu';

@Component({
  selector: 'app-sidenav',
  imports: [
    CommonModule,
    RouterModule,
    NestedNavigationComponent,
    TranslateModule,
    TimeFormatPipe,
    IndicatorComponent,
    MatChipsModule,
    MatIconModule,
    MatSelectModule,
    MatMenuModule,
    MatExpansionModule,
    SocketUserListComponent,
  ],
  standalone: true,
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({
        height: '0px',
        opacity: 0,
        overflow: 'hidden'
      })),
      state('expanded', style({
        height: '*',
        opacity: 1,
        overflow: 'hidden'
      })),
      transition('collapsed <=> expanded', [
        animate('180ms cubic-bezier(0.4,0,0.2,1)')
      ])
    ])
  ],
})

export class SidenavComponent implements OnDestroy {
  // Services
  private readonly authService: AuthService = inject(AuthService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly dashboardStore: DashboardStore = inject(DashboardStore);
  private readonly dashboardService: DashboardService = inject(DashboardService);
  private readonly router: Router = inject(Router);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly SidenavStore: SidenavStore = inject(SidenavStore);
  readonly uiTool: UiTool = inject(UiTool);

  // Component API
  @Input() isSidenavOpen: boolean = true;
  @Input() isMainLayout: boolean = false;
  @Output() toggleSidenav = new EventEmitter<void>();

  // Variables
  project: Project = defaultProject;
  menuNodes: TreeNode[] = [...SideMenu];
  mainNodes: TreeNode[] = [...MainMenu];
  selectedChildRoute: string = '';
  destroy$ = new Subject<void>();
  private lastMenuFilterKey: string = '';
  private readonly ROUTES_ALIASES: Record<string, string[]> = {
    'dashboard': ['dashboard'],
    'viewer/forge': ['forge', 'viewer/forge'],
    'viewer/maps': ['maps', 'viewer/maps'],
    'plannings': ['planning', 'plannings'],
    'report': ['report', 'timeline', 'events'],
    'risks': ['risks', 'risk'],
    'actions': ['actions', 'action'],
    'timelapse': ['enlaps', 'timelapse', 'timelapseGo'],
    'stream': ['stream', 'hse'],
    'informations': ['info'],
    'models': ['models'],
    'workflows': ['workflows', 'flow'],
    'project-settings/users': ['users'],
    'project-settings/resources': ['resources', 'resource'],
    'project-settings/security': ['privacy'],
    'project-settings/tags': ['tag'],
    'project-settings/database': ['exportdb'],
    'project-settings/kyd': ['kyd']
  };

  // Signals
  readonly isPrivileged: Signal<boolean> = this.authService.isPrivileged;
  readonly projectS: Signal<Project> = this.projectService.currentProjectSignal;
  readonly user: Signal<User> = this.authService.currentUser;
  readonly isLoadingProject: Signal<boolean> = this.projectService.isLoadingProject;
  isLandscape: boolean = true;

  // Computeds
  private readonly menuChildren = computed(() => {
    const dashboards = this.dashboardStore.sortedDashboards();
    const projectId = this.project?.id;

    if (!projectId || !dashboards.length) return [];

    return dashboards.map((dashboard, index) => {
      const projectSetting = dashboard.projectSettings?.find(s => s.project === projectId);
      const settingTitle = projectSetting?.title?.trim();
      const dashboardTitle = dashboard.title?.trim();

      return {
        icon: `filter_${index > 8 ? '9_plus' : index + 1}`,
        name: (settingTitle ? settingTitle : undefined) ?? (dashboardTitle ? dashboardTitle : undefined) ?? 'No title',
        route: `dashboard?dashboardId=${dashboard.id}`,
        children: []
      };
    });
  });

  readonly userLanguage = computed(
    () => this.authService.currentUser().language
  );

  get allowedPages(): string[] {
    return this.project?.datavizTabs ?? [];
  }

  get allowedSettingPages(): string[] {
    return this.project?.settingTabs ?? [];
  }

  constructor() {
    this.menuNodes[0] = SideMenu[0]!;
    this.menuNodes[0]!.action!.click = this.onDashboardSettings.bind(this);

    // ✅ Effect : Change project/role and load dashboards
    effect(() => {
      const project = this.projectService.currentProjectSignal();
      // Read the role here (tracked) so the menu is re-filtered on role change too — otherwise
      // switching accounts without navigating to a different project left the previous user's
      // (more permissive) menu cached, since only project.id was ever compared below.
      const role = this.user().role?.name;
      const filterKey = `${project?.id ?? ''}::${role ?? ''}`;

      if (!project?.id) {
        this.menuNodes = this.filterNodes([...SideMenu]);
        this.mainNodes = this.filterNodes([...MainMenu]);
        this.lastMenuFilterKey = filterKey;
        return;
      }

      if (this.lastMenuFilterKey === filterKey) {
        return;
      }

      const projectChanged = this.project?.id !== project.id;
      this.lastMenuFilterKey = filterKey;
      this.project = project;

      untracked(() => {
        this.menuNodes = this.filterNodes([...SideMenu]);
        this.mainNodes = this.filterNodes([...MainMenu]);
      });

      if (!projectChanged) return;

      this.dashboardStore.setCurrentProjectId(project.id);
      // The main layout hides the dashboards menu: the project layout loads them.
      if (this.isMainLayout) return;

      untracked(() => { this.menuNodes[0]!.loading = true; });

      this.dashboardService.getDashboards({ projects: project.id })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.menuNodes[0]!.loading = false;
          },
          error: (err) => {
            console.error('❌ Error loading dashboards in sidenav:', err);
            this.menuNodes[0]!.loading = false;
          }
        });
    });

    effect(() => {
      this.menuNodes[0]!.children = this.menuChildren();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  childAccessor = (node: TreeNode) => node.children;
  hasChild = (_: number, node: TreeNode) => !!node.children?.length;

  onDashboardSettings() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentDashboardId = urlParams.get('dashboardId');

    const editModal = this.dialog.open(DashboardManagerComponent, {
      width: '900px',
      height: '620px',
      data: {
        title: this.translate.instant('workflows.edition_dashboard'),
        currentDashboardId: currentDashboardId
      },
    });

    editModal.afterClosed().subscribe((res) => {
      if (res?.updated) {
        console.log('✅ Dashboard updated, store already synced');
      }
    });
  }

  expandAllNodes(node: TreeNode) {
    node.expanded = true;
    node.children?.forEach(child => this.expandAllNodes(child));
  }

  navigateToChild(route: string) {
    if (route) {
      const [path, queryString] = route.split('?');

      if (queryString) {
        const queryParams: { [key: string]: string } = {};

        queryString.split('&').forEach(param => {
          const [key, value] = param.split('=');

          if (key && value) {
            queryParams[key] = decodeURIComponent(value);
          }
        });

        this.router.navigate([path], { queryParams });
      } else {
        this.router.navigate([route]);
      }
    }
  }

  toggleSubMenu(node: TreeNode) {
    node.isOpen = !node.isOpen;
  }

  toggleSubMenuMain(node: TreeNode) {
    node.isOpen = !node.isOpen;
    this.SidenavStore.closeSidenav();
  }

  closeSidenav() {
    this.SidenavStore.closeSidenav();
  }

  onToggleSidenav() {
    this.SidenavStore.toggleSidenav();
    window.dispatchEvent(
      new CustomEvent('toggle-sidenav', { detail: { isOpen: this.SidenavStore.sidenavOpen } })
    );
  }

  filterNodes(nodes: TreeNode[]): TreeNode[] {
    const role = this.user().role?.name;

    // DEV : accès total
    if (role === 'dev') return nodes;

    // Interventions : réservé aux rôles dev et admin (userAdmin/user/guest exclus)
    const canSeeInterventions = role === 'admin';

    // ADMIN & USERADMIN : accès total (Paramètres de projet) et restreint (Visualisation)
    if (role === 'admin' || role === 'userAdmin') {
      return nodes.map(node => {
        if (node.name === 'menu.projects.params') {
          return {
            ...node,
            children: node.children ? node.children.filter(child => {
              if (child.route === 'interventions') return canSeeInterventions;
              return !child.route || this.isRouteAllowed(child.route, this.allowedSettingPages);
            }) : []
          };
        }

        if (node.name === 'menu.projects.title') {
          return {
            ...node,
            children: node.children ? node.children.filter(child => {
              // Image Merging : outil transverse, toujours visible pour admin (et dev, géré plus haut),
              // indépendamment des datavizTabs du projet.
              if (child.route === 'image-merging') return role === 'admin';
              return !child.route || this.isRouteAllowed(child.route, this.allowedPages);
            }) : []
          };
        }

        return {
          ...node,
          children: node.children ? this.filterNodes(node.children) : []
        };
      });
    }

    // USER & GUEST : accès restreint (Visualisation et Paramètres de projet)
    return nodes
      .filter(node => !node.route || this.isRouteAllowed(node.route, [...this.allowedPages, ...this.allowedSettingPages]))
      .map(node => ({
        ...node,
        children: node.children ? this.filterNodes(node.children) : []
      }));
  }

  private isRouteAllowed(route: string, allowedPages: string[]): boolean {
    return isRouteAllowed(route, allowedPages);
  }
}
